import { getPageRoutes } from "@/routes/pageRoutes";
import { useAppSelector } from "@/store";
import {
  useGetComingSoonQuery,
  useGetNextReleaseQuery,
} from "@/store/services/movie";
import { MovieCardProps, MovieWithDateFilter } from "@picovico/ui";
import { convertMinutesToHours } from "@picovico/utils";
import { differenceInDays, format } from "date-fns";
// import { useState } from "react";

const MovieList = ({
  title,
  showTimeList,
  toShowGenre,
  showReleaseType = "next_release",
  isNowShowing,
  releaseDateFormat = "MMMM d yyyy",
  ...props
}: MovieCardProps) => {
  // const [currentDate, setCurrentDate] = useState(
  //   format(new Date(), "yyyy-MM-dd"),
  // );
  const { advanceShowDays } = useAppSelector((state) => state.cineSetting);
  const { cinema_hall } = useAppSelector((state) => state.activeCinemaHall);
  const { company_idx } = useAppSelector((state) => state.config);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const {
    isLoading: isLoadingUpComing,
    isFetching: isFetchingUpComing,
    data: upComingData,
    refetch: upComingRefetch,
  } = useGetComingSoonQuery(
    { company_idx, cinema_hall },
    {
      skip: showReleaseType === "next_release",
      refetchOnReconnect: true,
      refetchOnFocus: true,
    },
  );
  const {
    isLoading: isLoadingNextRelease,
    isFetching: isFetchingNextRelease,
    data: nextReleaseData,
    refetch: nextReleaseRefetch,
  } = useGetNextReleaseQuery(
    { company_idx, cinema_hall },
    {
      skip: showReleaseType === "up_coming",
    },
  );

  const movieData = {
    next_release: nextReleaseData?.data,
    up_coming: upComingData?.data,
    combined: Array.from(
      new Map(
        [...(nextReleaseData?.data || []), ...(upComingData?.data || [])].map(
          (m) => [m?.idx, m],
        ),
      ).values(),
    ),
  };

  const isMovieLoading = {
    next_release: isLoadingNextRelease,
    up_coming: isLoadingUpComing,
    combined: isLoadingNextRelease || isLoadingUpComing,
  };
  const isMovieFetching = {
    next_release: isFetchingNextRelease,
    up_coming: isFetchingUpComing,
    combined: isFetchingNextRelease || isFetchingUpComing,
  };
  const movieRefetch = {
    next_release: nextReleaseRefetch,
    up_coming: upComingRefetch,
    combined: () => {
      if (!cinema_hall || !company_idx) return;
      nextReleaseRefetch();
      upComingRefetch();
    },
  };

  const movieCardProps = {
    movies:
      movieData[showReleaseType]?.map(
        ({
          idx,
          grade,
          duration,
          poster,
          release_date,
          genre,
          tag,
          roll_quality,
          ...item
        }) => {
          const releaseDateObj = new Date(release_date);
          const daysLeft = differenceInDays(releaseDateObj, today);

          let ribbonText: string | undefined;
          if (daysLeft > 0) {
            ribbonText = `${daysLeft} day${daysLeft === 1 ? "" : "s"} left`;
          } else if (daysLeft === 0) {
            ribbonText = "Releasing Today";
          }

          return {
            title: item.name,
            poster,
            grade,
            roll_quality,
            genre,
            tag,
            duration: convertMinutesToHours(duration),
            toShowGenre,
            idx,
            releaseDate: format(releaseDateObj, releaseDateFormat),
            videoLink: item.trailer,
            detailLink: getPageRoutes(
              "movie_detail",
              {
                movieIdx: idx,
                cinemaHallIdx: cinema_hall as string,
              },
              {
                startDate: item?.movieinfo?.show_start_date
                  ? format(item?.movieinfo?.show_start_date, "yyyy-MM-dd")
                  : "",
                // endDate: item.movieinfo?.show_end_date,
              },
            ),
            isNowShowing,
            showTimeList,
            ribbonText,
          };
        },
      ) || [],
    dateSelectorMaxDays: advanceShowDays,
    title,
    isLoading:
      isMovieLoading[showReleaseType] || isMovieFetching[showReleaseType],

    onDateChange: () => {
      // setCurrentDate(format(date, "yyyy-MM-dd"));
      movieRefetch[showReleaseType]();
    },

    ...props,
  };

  return cinema_hall && <MovieWithDateFilter {...movieCardProps} />;
};

export default MovieList;
